<script setup>
/**
 * O telefone na barra do topo, ao lado do sino. Aparece só para quem tem ramal.
 * O ponto diz se o telefone está pronto; durante a chamada o botão vira um
 * relógio verde que devolve a pessoa para a chamada.
 */
import { computed, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTelefone, abrirPainel } from './estado';
import { formatarDuracao } from './telefone';

const { t } = useI18n();
const telefone = useTelefone();

const visivel = computed(
  () => !['inicio', 'sem_ramal'].includes(telefone.fase)
);
const emChamada = computed(() => telefone.chamada?.fase === 'em_curso');
// tocando: o botão chacoalha e pulsa até alguém atender (rodada de design)
const tocando = computed(() =>
  telefone.chamadas.some(c => c.fase === 'tocando')
);
// o ponto diz o status da pessoa quando o telefone está pronto
const ponto = computed(() =>
  telefone.fase === 'pronto' && telefone.ramal?.status
    ? `status-${telefone.ramal.status}`
    : telefone.fase
);

const agora = ref(Date.now());
let relogio = null;
watch(
  emChamada,
  sim => {
    clearInterval(relogio);
    if (!sim) return;
    relogio = setInterval(() => {
      agora.value = Date.now();
    }, 1000);
  },
  { immediate: true }
);
onUnmounted(() => clearInterval(relogio));

const tempo = computed(() =>
  emChamada.value
    ? formatarDuracao(agora.value - telefone.chamada.atendidaEm)
    : ''
);
const rotulo = computed(() =>
  t(`TELEFONE.FASE.${telefone.fase.toUpperCase()}`)
);
</script>

<template>
  <button
    v-if="visivel"
    type="button"
    class="tel-botao"
    :class="{
      'tel-botao--aberto': telefone.painelAberto,
      'tel-botao--chamada': emChamada,
      'tel-botao--tocando': tocando,
    }"
    :aria-label="t('TELEFONE.ABRIR', { estado: rotulo })"
    :aria-expanded="telefone.painelAberto"
    @click="abrirPainel()"
  >
    <span class="i-ph-phone-fill tel-botao__ico" aria-hidden="true" />
    <span v-if="emChamada" class="tel-botao__tempo">{{ tempo }}</span>
    <span
      v-else
      class="tel-botao__ponto"
      :class="`tel-botao__ponto--${ponto}`"
      aria-hidden="true"
    />
  </button>
</template>

<style scoped>
.tel-botao {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--esp-1);
  min-width: 2rem;
  height: 2rem;
  padding: 0 var(--esp-2);
  border-radius: var(--r-3);
  color: rgb(var(--slate-11));
  transition:
    background var(--mov-1) var(--mov-curva),
    color var(--mov-1) var(--mov-curva);
}
.tel-botao:hover {
  background: rgb(255 255 255 / 8%);
  color: rgb(var(--slate-12));
}
.tel-botao--aberto {
  background: rgb(255 255 255 / 12%);
  color: rgb(var(--slate-12));
}
.tel-botao:focus-visible {
  outline: 2px solid rgb(var(--blue-9));
  outline-offset: 1px;
}
.tel-botao__ico {
  width: var(--ico-2);
  height: var(--ico-2);
}
.tel-botao__ponto {
  position: absolute;
  top: 5px;
  right: 5px;
  width: 8px;
  height: 8px;
  border-radius: var(--r-redondo);
  box-shadow: 0 0 0 2px rgb(var(--slate-1));
  background: rgb(var(--slate-9));
}
.tel-botao--tocando {
  color: #fff;
  background: rgb(var(--teal-9));
  animation: tel-botao-pulso 1.2s var(--mov-curva) infinite;
}
.tel-botao--tocando .tel-botao__ico {
  animation: tel-botao-chacoalha 1.2s ease-in-out infinite;
}
@keyframes tel-botao-pulso {
  0% {
    box-shadow: 0 0 0 0 rgb(var(--teal-9) / 55%);
  }
  100% {
    box-shadow: 0 0 0 12px rgb(var(--teal-9) / 0%);
  }
}
@keyframes tel-botao-chacoalha {
  0%,
  50%,
  100% {
    transform: rotate(0);
  }
  10%,
  30% {
    transform: rotate(-14deg);
  }
  20%,
  40% {
    transform: rotate(14deg);
  }
}
@media (prefers-reduced-motion: reduce) {
  .tel-botao--tocando,
  .tel-botao--tocando .tel-botao__ico {
    animation: none;
  }
}
.tel-botao__ponto--status-disponivel {
  background: rgb(var(--ativo));
}
.tel-botao__ponto--status-almoco,
.tel-botao__ponto--status-reuniao {
  background: rgb(var(--amber-9));
}
.tel-botao__ponto--status-nao_perturbe {
  background: rgb(var(--ruby-9));
}
.tel-botao__ponto--pronto {
  background: rgb(var(--ativo));
}
.tel-botao__ponto--conectando {
  background: rgb(var(--amber-9));
}
.tel-botao__ponto--erro {
  background: rgb(var(--ruby-9));
}

/* Em chamada: a pílula verde com o relógio — a volta para a chamada. */
.tel-botao--chamada,
.tel-botao--chamada:hover {
  background: rgb(var(--pg-teal-9));
  color: rgb(255 255 255);
}
.tel-botao__tempo {
  font-size: var(--txt-nota);
  font-weight: var(--peso-forte);
  line-height: var(--linha-apertada);
  font-variant-numeric: tabular-nums;
}
</style>
